import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiSearch,
  FiHeart,
  FiCalendar,
  FiMessageCircle,
  FiX,
  FiChevronRight,
} from 'react-icons/fi';
import { MyContext } from '../Context/MyContext';
import {
  ACTIVITY_META,
  getRecentActivities,
  isTimelineClosed,
  setTimelineClosed,
} from '../utils/userActivity';
import './ActivityTimeline.css';

const TYPE_ICONS = {
  search: FiSearch,
  wishlist: FiHeart,
  booking: FiCalendar,
  enquiry: FiMessageCircle,
};

const TYPE_STYLES = {
  search:   { accent: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
  wishlist: { accent: '#e11d48', bg: '#fff1f2', border: '#fecdd3' },
  booking:  { accent: '#c8a04a', bg: '#fffbeb', border: '#fde68a' },
  enquiry:  { accent: '#0d9488', bg: '#ecfdf5', border: '#99f6e4' },
};

const fmtTime = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  const diff = Date.now() - d.getTime();
  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

const ActivityTimeline = () => {
  const { user } = useContext(MyContext);
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadActivities = useCallback(async () => {
    setLoading(true);
    try {
      if (isTimelineClosed()) {
        setActivities([]);
        setVisible(false);
        return;
      }
      const rows = await getRecentActivities(user, 3);
      setActivities(rows);
      setVisible(rows.length > 0);
    } finally {
      setLoading(false);
    }
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadActivities();
    const onUpdate = () => loadActivities();
    window.addEventListener('user-activity-updated', onUpdate);
    window.addEventListener('recent-search-updated', onUpdate);
    return () => {
      window.removeEventListener('user-activity-updated', onUpdate);
      window.removeEventListener('recent-search-updated', onUpdate);
    };
  }, [loadActivities]);

  const handleAction = (item) => {
    if (item.url) navigate(item.url);
    setVisible(false);
  };

  const handleClose = () => {
    setTimelineClosed(true);
    setVisible(false);
  };

  if (loading || !visible || !activities.length) return null;

  return (
    <section className="activity-timeline-section" aria-label="Activity timeline">
      <div className="activity-timeline-card">
        <div className="activity-timeline__header">
          <div className="activity-timeline__heading">
            <p className="activity-timeline__eyebrow">Continue where you left off</p>
            <h2 className="activity-timeline__title">Activity Timeline</h2>
          </div>
          <button
            type="button"
            className="activity-timeline__close"
            onClick={handleClose}
            aria-label="Close timeline"
          >
            <FiX size={16} />
          </button>
        </div>

        <ul className="activity-timeline__list">
          {activities.map((item, index) => {
            const meta = ACTIVITY_META[item.type] || ACTIVITY_META.search;
            const style = TYPE_STYLES[item.type] || TYPE_STYLES.search;
            const Icon = TYPE_ICONS[item.type] || FiSearch;

            return (
              <li
                key={`${item.type}-${item.url}-${item.acted_at}-${index}`}
                className="activity-timeline__item"
              >
                <div className="activity-timeline__rail" aria-hidden="true">
                  <span
                    className="activity-timeline__dot"
                    style={{ background: style.accent, boxShadow: `0 0 0 3px ${style.bg}` }}
                  />
                  {index < activities.length - 1 && <span className="activity-timeline__line" />}
                </div>

                <div className="activity-timeline__body">
                  <div className="activity-timeline__item-main">
                    <span
                      className="activity-timeline__icon"
                      style={{
                        background: style.bg,
                        borderColor: style.border,
                        color: style.accent,
                      }}
                      aria-hidden="true"
                    >
                      <Icon size={18} strokeWidth={2} />
                    </span>

                    <div className="activity-timeline__copy">
                      <span className="activity-timeline__tag" style={{ color: style.accent }}>
                        {meta.tag}
                      </span>
                      <p className="activity-timeline__label" title={item.label}>
                        {item.label}
                      </p>
                      <p className="activity-timeline__time">{fmtTime(item.acted_at)}</p>
                    </div>
                  </div>

                  <button
                    type="button"
                    className="activity-timeline__action"
                    style={{ borderColor: style.accent, color: style.accent }}
                    onClick={() => handleAction(item)}
                  >
                    {meta.action}
                    <FiChevronRight size={14} />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
};

export default ActivityTimeline;
